import { useState } from "react";
import { toast } from "sonner";
import { RefreshCw, FlaskConical } from "lucide-react";
import { Button } from "../components/ui/button";
import { Page, Panel } from "../components/Layout";
import { api, notifyErr, fmtBytes, fmtDate } from "../lib/api";
import { usePoll } from "../lib/hooks";
import { CheckBadge } from "../lib/labels";

const NAMES = [["frontend", "Frontend"], ["backend", "Backend API"], ["database", "Database PostgreSQL"], ["vicidial", "Connessioni AlFa Dial"],
  ["recording_servers", "Server registrazioni"], ["whisper", "faster-whisper (trascrizione)"], ["ollama", "Ollama (inferenza)"], ["ai_models", "Modelli AI installati"],
  ["audio", "Decodifica audio (FFmpeg/PyAV)"], ["worker", "Worker elaborazione audio"], ["queue", "Code job"], ["storage", "Permessi archivio"], ["excel", "Esportazione Excel"]];

export default function Diagnostica() {
  const [d, setD] = useState(null);
  const [testing, setTesting] = useState({});
  const load = () => api.get("/diagnostics").then((r) => setD(r.data)).catch(notifyErr);
  usePoll(load, 15000);
  const runTest = async (kind) => {
    setTesting({ ...testing, [kind]: true });
    try {
      const j = (await api.post(`/diagnostics/test/${kind}`)).data;
      for (let i = 0; i < 120; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        const s = (await api.get(`/diagnostics/test-job/${j.id}`)).data;
        if (s.status === "completed") { toast.success(`Test ${kind} riuscito`); break; }
        if (s.status === "failed") { toast.error(`Test ${kind} fallito: ${s.error}`); break; }
      }
      load();
    } catch (e) { notifyErr(e); } finally { setTesting((t) => ({ ...t, [kind]: false })); }
  };
  const checks = d ? { frontend: { status: "ok", detail: "Interfaccia caricata e collegata al backend" }, ...d.checks } : {};
  const sys = d?.system;
  return (
    <Page title="Diagnostica Sistema" subtitle="Ogni stato «Operativo» deriva da un controllo reale eseguito ora o da un'elaborazione reale riuscita." testid="diagnostica-page"
      actions={<>
        <Button variant="outline" className="rounded-sm h-8" onClick={() => runTest("whisper")} disabled={testing.whisper} data-testid="test-whisper-btn"><FlaskConical size={14} className="mr-1" />{testing.whisper ? "Trascrizione in corso…" : "Test trascrizione reale"}</Button>
        <Button variant="outline" className="rounded-sm h-8" onClick={() => runTest("ollama")} disabled={testing.ollama} data-testid="test-ollama-btn"><FlaskConical size={14} className="mr-1" />{testing.ollama ? "Inferenza in corso…" : "Test inferenza Ollama"}</Button>
        <Button className="rounded-sm h-8 bg-[#0047FF]" onClick={load} data-testid="refresh-diagnostics-btn"><RefreshCw size={14} className="mr-1" />Aggiorna</Button>
      </>}>
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3 mb-4">
        {NAMES.map(([k, l]) => checks[k] && (
          <div key={k} className="bg-white border rounded-sm p-3" data-testid={`check-${k}`}>
            <div className="flex items-center justify-between"><span className="text-sm font-medium">{l}</span><CheckBadge status={checks[k].status} testid={`check-status-${k}`} /></div>
            <div className="text-xs text-neutral-600 mt-1.5">{checks[k].detail}</div>
          </div>
        ))}
      </div>
      {sys && <Panel title="Risorse server" className="mb-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
          <div><div className="eyebrow">Sistema</div>{sys.os} · {sys.arch}</div><div><div className="eyebrow">CPU</div>{sys.cpu_count} core · load {sys.load_avg.map((x) => x.toFixed(2)).join(" ")}</div>
          <div><div className="eyebrow">RAM host</div>{fmtBytes(sys.ram_available)} liberi / {fmtBytes(sys.ram_total)}</div>
          <div><div className="eyebrow">Limite RAM container</div>{sys.cgroup_memory_limit ? fmtBytes(sys.cgroup_memory_limit) : "nessuno"}</div>
          <div><div className="eyebrow">Worker RAM</div>{checks.worker?.rss_mb} MB (picco {checks.worker?.peak_rss_mb} MB)</div>
        </div>
      </Panel>}
      <div className="bg-[#0A0A0A] text-neutral-200 rounded-sm p-4 font-mono text-[11px] overflow-x-auto" data-testid="diagnostics-console">
        <div className="text-neutral-500 mb-2">$ sales-rescue diagnostics --json · {fmtDate(d?.generated_at)}</div>
        <pre className="whitespace-pre-wrap">{d ? JSON.stringify({ checks: d.checks, last_tests: d.last_tests }, null, 2) : "caricamento…"}</pre>
      </div>
    </Page>
  );
}
